'use client';

import { __assign, __spreadArray } from '../_virtual/_tslib.js';
import React__default, { memo, useReducer, useState, useRef, useMemo, useEffect } from 'react';
import useFloating from '../hooks/useFloating/index.js';
import useClickAway from '../hooks/useClickAway.js';
import '../hooks/useMakeSearchable/types.js';
import { ELanguage } from '../Utils/constants.js';
import { isLocationChanged } from '../Utils/helpers.js';
import '../services/resumes/types.js';
import InputContainer from './components/InputContainer/index.js';
import MobileDialog from './components/MobileDialog/index.js';
import translations from './translations.js';
import { TreeSelectWrapper } from './TreeSelect.css.js';
import { DEFAULT_SELECT_OPTIONS, DEFAULT_DROPDOWN_STYLE_OPTIONS, SELECT_CATE_LEVEL_PARENT, SELECT_CATE_LEVEL_SUB } from './constants.js';
import { treeSelectReducer, TreeSelectContext } from './context/reducer.js';
import { initialTreeSelect } from './context/index.js';
import { TreeSelectAction } from './context/types.js';
import { sortOptionsAlphabet, sortChildOptionAlphabet, deepCloneArray } from './helpers.js';

var Dropdown = React__default.lazy(function () { return import('./components/Dropdown/index.js'); });
var TreeSelect = function (props) {
    var langCode = props.langCode, textOptions = props.textOptions, selectOptions = props.selectOptions, _a = props.hasError, hasError = _a === void 0 ? false : _a, _b = props.isMobile, isMobile = _b === void 0 ? false : _b, _c = props.isEnableFooter, isEnableFooter = _c === void 0 ? false : _c, _d = props.isDisabled, isDisabled = _d === void 0 ? false : _d, _e = props.options, options = _e === void 0 ? [] : _e, _f = props.childrenList, childrenList = _f === void 0 ? {} : _f, _g = props.value, value = _g === void 0 ? [] : _g, dropdownStyleOptions = props.dropdownStyleOptions, styleOptions = props.styleOptions, onValueChange = props.onValueChange, onRemoveEachValue = props.onRemoveEachValue, onCloseDropdown = props.onCloseDropdown, onShowDropdown = props.onShowDropdown, floatingConfig = props.floatingConfig, onBlur = props.onBlur, onApplyChangeProps = props.onClickApply, onClickCancel = props.onClickCancel, isDisabledClearValue = props.isDisabledClearValue, forResponsive = props.forResponsive;
    var _h = floatingConfig || {}, _j = _h.placement, placement = _j === void 0 ? 'bottom-start' : _j, _k = _h.position, position = _k === void 0 ? 'absolute' : _k, _l = _h.useWindowAsFlippingContainer, useWindowAsFlippingContainer = _l === void 0 ? false : _l, _m = _h.enablePortal, enablePortal = _m === void 0 ? false : _m, _o = _h.isFlip, isFlip = _o === void 0 ? true : _o;
    var mergedSelectOptions = __assign(__assign({}, DEFAULT_SELECT_OPTIONS), selectOptions);
    var mergedDropdownStyleOptions = __assign(__assign({}, DEFAULT_DROPDOWN_STYLE_OPTIONS), dropdownStyleOptions);
    var _p = useReducer(treeSelectReducer, initialTreeSelect), treeSelectState = _p[0], dispatch = _p[1];
    // Dropdown state
    var _q = useState(false), isVisible = _q[0], setElementVisible = _q[1];
    var _r = useFloating({
        placement: placement,
        position: position,
        config: {
            useWindowAsFlippingContainer: useWindowAsFlippingContainer,
            isFlip: isFlip,
            detectHidden: !mergedDropdownStyleOptions.isForceClose
        }
    }), refs = _r.refs, context = _r.context, FloatingPortal = _r.FloatingPortal;
    // Value
    var _s = useState(value), selectedValue = _s[0], setSelectedValue = _s[1];
    // Mobile temporary Value
    var _t = useState(value), tempSelectedValue = _t[0], setTempSelectedValue = _t[1];
    // Dropdown state
    var _u = useState(true), isFirstLevelMounted = _u[0], setFirstLevelMounted = _u[1];
    var _v = useState(false), isSecondLevelMounted = _v[0], setSecondLevelMounted = _v[1];
    var resetSelectView = function () {
        if (!mergedSelectOptions.isSingleSelect) {
            return;
        }
        dispatch({
            type: TreeSelectAction.UPDATE_SELECT_LEVEL,
            payload: 0
        });
        dispatch({
            type: TreeSelectAction.UPDATE_SELECTED_PARENT_ID,
            payload: -1
        });
        setFirstLevelMounted(true);
        setSecondLevelMounted(false);
    };
    useClickAway(function () {
        resetSelectView();
        setElementVisible(false);
        onBlur && onBlur();
    }, {
        nodeRefs: [refs.popup, refs.reference]
    });
    // End dropdown state
    var sortedOptions = sortOptionsAlphabet(__spreadArray([], options, true), mergedSelectOptions.isSortAlphabet);
    var sortedChildOptions = sortChildOptionAlphabet(__assign({}, childrenList), langCode, mergedSelectOptions.isSortAlphabet);
    // isApply Click
    var isApplyClick = useRef(false);
    var isAlreadyOpened = useRef(false);
    var bodyNode = typeof document !== 'undefined' ? document === null || document === void 0 ? void 0 : document.querySelector('body') : null;
    var t = function (text) {
        var _a;
        if (langCode === ELanguage.EN) {
            return text;
        }
        else {
            return (_a = translations[text]) !== null && _a !== void 0 ? _a : text;
        }
    };
    // this cb handle for case just apply value changed when
    var dispatchValueChanged = function (clonedSelectedValue) {
        // Only callback onValueChange when value is different with value from prop
        if (value.length === 0 && clonedSelectedValue.length === 0) {
            return;
        }
        var isChange = isLocationChanged(value, clonedSelectedValue);
        if (!isChange) {
            return;
        }
        onValueChange && onValueChange(clonedSelectedValue);
    };
    var onRemoveItem = function (id) {
        var clonedSelectedValue = __spreadArray([], selectedValue, true).filter(function (item) { return item.parentId !== id; });
        setSelectedValue(clonedSelectedValue);
        dispatchValueChanged(clonedSelectedValue);
        if (onRemoveEachValue) {
            var itemRemove = selectedValue.find(function (item) { return item.parentId === id; });
            onRemoveEachValue(clonedSelectedValue, itemRemove);
        }
        setTempSelectedValue(clonedSelectedValue);
    };
    var onSetSelectedValue = function (value) {
        var clonedValue = __spreadArray([], value, true);
        // Do update temporary selectedValue when isMobile or isEnableFooter
        if (!(selectOptions === null || selectOptions === void 0 ? void 0 : selectOptions.isSingleSelect) && (isMobile || isEnableFooter)) {
            setTempSelectedValue(clonedValue);
            return;
        }
        if (selectOptions === null || selectOptions === void 0 ? void 0 : selectOptions.isSingleSelect) {
            setTempSelectedValue(clonedValue);
        }
        setSelectedValue(clonedValue);
        dispatchValueChanged(clonedValue);
        if ((selectOptions === null || selectOptions === void 0 ? void 0 : selectOptions.isSingleSelect) && clonedValue.length > 0) {
            setElementVisible(false);
        }
    };
    var onClickApply = function () {
        isApplyClick.current = true;
        // When apply button clicked, set selectedValue from tempSelectedValue
        setSelectedValue(tempSelectedValue);
        dispatchValueChanged(tempSelectedValue);
        onApplyChangeProps && onApplyChangeProps(tempSelectedValue);
    };
    var resetTemporarySelectedValue = function () {
        if (isApplyClick.current) {
            isApplyClick.current = false;
            return;
        }
        if (isVisible) {
            return;
        }
        // Reset tempSelectedValue when apply is not triggered
        setTempSelectedValue(selectedValue);
    };
    var getInputContainerValue = function () {
        if (isApplyClick.current && isMobile) {
            return tempSelectedValue;
        }
        if (isEnableFooter) {
            return tempSelectedValue;
        }
        return deepCloneArray(selectedValue);
    };
    var updateMountedLevel = function (level) {
        if (level === SELECT_CATE_LEVEL_PARENT) {
            return;
        }
        setFirstLevelMounted(false);
        setSecondLevelMounted(true);
    };
    var checkAndUpdateHistory = function () {
        // This function is not ready for mutiple select yet
        // TODO: make this into option that allow to show current view from history
        if (!mergedSelectOptions.isSingleSelect) {
            return;
        }
        var history = treeSelectState.history, selectLevel = treeSelectState.selectLevel, selectedParentId = treeSelectState.selectedParentId;
        var isHistoryChange = false;
        if (selectLevel !== history.selectLevel) {
            // Update select level
            dispatch({
                type: TreeSelectAction.UPDATE_SELECT_LEVEL,
                payload: history.selectLevel
            });
            updateMountedLevel(history.selectLevel);
            isHistoryChange = true;
        }
        if (selectedParentId !== history.selectedParentId) {
            // Update select parent
            dispatch({
                type: TreeSelectAction.UPDATE_SELECTED_PARENT_ID,
                payload: history.selectedParentId
            });
            isHistoryChange = true;
        }
        // Check history change
        if (isHistoryChange) {
            return;
        }
        // Check value and set level
        if (value.length === 0) {
            return;
        }
        var levelFromValue = value[0].parentId === -1 ? SELECT_CATE_LEVEL_PARENT : SELECT_CATE_LEVEL_SUB;
        dispatch({
            type: TreeSelectAction.UPDATE_SELECT_LEVEL,
            payload: levelFromValue
        });
        dispatch({
            type: TreeSelectAction.UPDATE_SELECTED_PARENT_ID,
            payload: value[0].parentId
        });
        dispatch({
            type: TreeSelectAction.UPDATE_HISTORY,
            payload: {
                selectedParentId: value[0].parentId,
                selectLevel: levelFromValue
            }
        });
        updateMountedLevel(levelFromValue);
    };
    var onClickInputContainer = function () {
        var delayTime = mergedDropdownStyleOptions.delayTime;
        if (delayTime && delayTime > 0) {
            if (isVisible) {
                setElementVisible(false);
            }
            else {
                setTimeout(function () {
                    setElementVisible(true);
                }, delayTime);
            }
        }
        else {
            setElementVisible(!isVisible);
        }
        onShowDropdown && onShowDropdown(!isVisible);
        if (!isVisible) {
            return;
        }
        resetSelectView();
    };
    // We have a issue when parent component change
    // make treeselect component infinity loop
    // this hook useMemo help by pass this error
    var memoValueProps = useMemo(function () {
        if (!value || value.length < 1) {
            return null;
        }
        return value;
    }, [value]);
    useEffect(function () {
        var _a;
        if (!memoValueProps) {
            if (isMobile || isEnableFooter) {
                setTempSelectedValue([]);
            }
            setSelectedValue([]);
            return;
        }
        var mappedValues = [];
        // Re-mapped list with empty child
        for (var i = 0; i < memoValueProps.length; i++) {
            var valueItem = memoValueProps[i];
            if (!(valueItem === null || valueItem === void 0 ? void 0 : valueItem.parentId)) {
                continue;
            }
            mappedValues.push({
                parentId: valueItem === null || valueItem === void 0 ? void 0 : valueItem.parentId,
                childrenIds: (_a = valueItem === null || valueItem === void 0 ? void 0 : valueItem.childrenIds) !== null && _a !== void 0 ? _a : [-1]
            });
        }
        // Make sure filter out undefined and null value
        var filteredValues = mappedValues.filter(function (item) { return item !== undefined && item !== null; });
        // only setMobileSelectedValue for mobile or Desktop with option Enable Footer
        if (isMobile || isEnableFooter) {
            setTempSelectedValue(filteredValues);
        }
        setSelectedValue(filteredValues);
    }, [isMobile, memoValueProps, isEnableFooter]);
    useEffect(function () {
        if (isVisible) {
            checkAndUpdateHistory();
            isAlreadyOpened.current = true;
            return;
        }
        if (isEnableFooter) {
            resetTemporarySelectedValue();
        }
        if (!isAlreadyOpened.current) {
            return;
        }
        onCloseDropdown && onCloseDropdown(selectedValue, isVisible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, isAlreadyOpened]);
    useEffect(function () {
        if (!(mergedDropdownStyleOptions === null || mergedDropdownStyleOptions === void 0 ? void 0 : mergedDropdownStyleOptions.isForceClose)) {
            return;
        }
        var onForceClose = function () {
            setElementVisible(false);
            return function () {
                window.removeEventListener('scroll', onForceClose);
            };
        };
        window.addEventListener('scroll', onForceClose);
    }, [mergedDropdownStyleOptions === null || mergedDropdownStyleOptions === void 0 ? void 0 : mergedDropdownStyleOptions.isForceClose]);
    var renderDropdown = function () {
        var _a;
        var parsedSelectedValue = isMobile || isEnableFooter ? tempSelectedValue : selectedValue;
        var isEnableFooterChecked = isMobile ? true : isEnableFooter;
        if (bodyNode === null) {
            return React__default.createElement(React__default.Fragment, null);
        }
        var width;
        if (dropdownStyleOptions === null || dropdownStyleOptions === void 0 ? void 0 : dropdownStyleOptions.width) {
            width = dropdownStyleOptions === null || dropdownStyleOptions === void 0 ? void 0 : dropdownStyleOptions.width;
        }
        if (!(dropdownStyleOptions === null || dropdownStyleOptions === void 0 ? void 0 : dropdownStyleOptions.width) && context.position === 'fixed') {
            width = "".concat((_a = refs.reference.current) === null || _a === void 0 ? void 0 : _a.offsetWidth, "px");
        }
        var dropdownStyle = isMobile
            ? {}
            : {
                position: context.position,
                top: context.y,
                left: context.x,
                display: context.isReferenceHidden ? 'none' : 'block',
                width: width,
                marginTop: mergedDropdownStyleOptions.marginTop
            };
        return (React__default.createElement(FloatingPortal, { enable: isMobile ? false : enablePortal },
            React__default.createElement(React__default.Suspense, { fallback: React__default.createElement(React__default.Fragment, null) },
                React__default.createElement(Dropdown, { ref: refs.setPopup, isShow: isVisible, isMobile: isMobile, options: sortedOptions, childrenList: sortedChildOptions, selectOptions: selectOptions, selectedValue: parsedSelectedValue, setSelectedValue: onSetSelectedValue, firstLevelMount: [isFirstLevelMounted, setFirstLevelMounted], secondLevelMount: [isSecondLevelMounted, setSecondLevelMounted], t: t, textOptions: {
                        headerLabel: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.headerLabel) || t('Select'),
                        searchPlaceholder: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.searchPlaceholder) || t('Search'),
                        allText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.allOptionText) || t('All'),
                        subOptionText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.subOptionText) || t('districts'),
                        selectAllText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.selectAllText) || t('All'),
                        maxLimitText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.maxLimitText) || t('You have selected up to three options'),
                        removeAllText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.removeAllText) || t('Remove all'),
                        backText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.backText) || t('Back'),
                        noResultText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.noResultText) || t('No result'),
                        selectedCountText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.selectedCountText) || '',
                        primaryButtonText: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.primaryButtonText) || ''
                    }, footerOptions: {
                        isEnableFooter: isEnableFooterChecked,
                        setElementVisible: setElementVisible,
                        onClickApply: onClickApply,
                        onClickCancel: onClickCancel,
                        resetSelectedValue: function () { return setTempSelectedValue(selectedValue); }
                    }, langCode: langCode, styleOptions: dropdownStyleOptions, style: dropdownStyle, placement: context.placement }))));
    };
    return (React__default.createElement(TreeSelectContext.Provider, { value: {
            state: treeSelectState,
            dispatch: dispatch
        } },
        React__default.createElement(TreeSelectWrapper, { "data-id": 'tree-select', cssString: styleOptions === null || styleOptions === void 0 ? void 0 : styleOptions.cssString, isDisabled: isDisabled },
            React__default.createElement(InputContainer, { placeholder: (textOptions === null || textOptions === void 0 ? void 0 : textOptions.inputPlaceholder) || t('All'), isError: hasError, isDisabledClearValue: isDisabledClearValue, value: getInputContainerValue(), options: sortedOptions, isDisabled: isDisabled, onClick: onClickInputContainer, childrenList: sortedChildOptions, onRemoveItem: function (id) { return onRemoveItem(id); }, styleOptions: styleOptions, selectOptions: selectOptions, dropdownStyleOptions: mergedDropdownStyleOptions, langCode: langCode, ref: refs.setReference, markHasValue: tempSelectedValue.length > 0, forResponsive: forResponsive }),
            isMobile ? (React__default.createElement(MobileDialog, { isShow: isVisible, onUnmount: resetTemporarySelectedValue }, renderDropdown())) : (renderDropdown()))));
};
var TreeSelect$1 = memo(TreeSelect);

export { TreeSelect$1 as default };
//# sourceMappingURL=index.js.map
